import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import { Loading } from 'js/components';
import { CycleTime } from 'js/components/Charts/CycleTime';
import { Velocity } from 'js/components/Charts/Sprint/Velocity';
import { Churn } from 'js/components/Charts/Sprint/Churn';
import ConnectorErrorCard from './ConnectorErrorCard';

const ESTIMATION_OPTIONS = [
  { value: 'tasks', label: 'Work item count' },
  { value: 'story_points', label: 'Story points' },
  { value: 'hours', label: 'Time' },
];

/**
 * Presentational Jira team page: cycle time, velocity, churn.
 * Receives reports from TeamPageContainer.
 * units: configured default estimate field (tasks | story_points | hours).
 * removedStatuses: the team's statuses for dropped work; countRemovedAsCompleted overrides
 * them so the reports count that work as completed instead.
 */
function JiraTeamPage({
  reports,
  report,
  isLoading,
  error,
  units = 'tasks',
  removedStatuses = [],
  countRemovedAsCompleted = false,
  onCountRemovedAsCompletedChange = () => {},
}) {
  const [estimationType, setEstimationType] = useState(units);

  useEffect(() => {
    setEstimationType(units);
  }, [units]);

  const estimationTypeLabel = useMemo(() => {
    return ESTIMATION_OPTIONS.find((option) => option.value === estimationType)
      ?.label;
  }, [estimationType]);

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <Loading />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return <ConnectorErrorCard error={error} />;
  }

  if (!reports?.cycleTime?.report || !reports?.cycleTime?.report?.summary) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          Jira Metrics
        </CardTitle>
        <div>
          <Label for="jira-estimation-type">Estimation field</Label>

          <Input
            id="jira-estimation-type"
            type="select"
            value={estimationType}
            onChange={(e) => setEstimationType(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 'auto', minWidth: '10rem' }}
          >
            {ESTIMATION_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Input>
          {removedStatuses.length > 0 && (
            <FormGroup check className="mt-2">
              <Input
                id="jira-count-removed-as-completed"
                type="checkbox"
                checked={countRemovedAsCompleted}
                onChange={(e) =>
                  onCountRemovedAsCompletedChange(e.target.checked)
                }
              />
              <Label check for="jira-count-removed-as-completed">
                Count {removedStatuses.join(', ')} as completed
              </Label>
            </FormGroup>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {reports?.cycleTime?.report?.tasks?.length > 0 && (
          <div className="mb-4">
            <h6>Cycle Time</h6>
            <CycleTime tasks={reports.cycleTime.report.tasks} height={400} />
          </div>
        )}
        {reports?.sprints?.report && (
          <>
            <div className="mb-4">
              <h6 className="mb-0">
                Sprint Delivery <small>({estimationTypeLabel})</small>
              </h6>
              <div style={{ margin: '0 -15px' }}>
                <Velocity
                  sprints={reports.sprints.report}
                  height={400}
                  units={estimationType}
                />
              </div>
            </div>
            <div className="mb-4">
              <h6 className="mb-0">
                Sprint Stability <small>({estimationTypeLabel})</small>
              </h6>
              <div style={{ margin: '0 -15px' }}>
                <Churn
                  sprints={reports.sprints.report}
                  height={400}
                  units={estimationType}
                />
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default JiraTeamPage;
